import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { fetchWrapper } from '../../utils/apiHandlers'
import Button from '../Button'
import SmallButton from '../SmallButton'
import InputComp from '../InputComp'
import LoadingComponent from '../LoadingComponent'
import MessageModal from '../MessageModal'
import theme from '../../theme'

const ATTR_LABELS: { [key: string]: string } = {
  outdoor_seating: "Outdoor seating", dog_friendly: "Dog-friendly", reservations: "Reservations",
  parking: "Parking", kids: "Kids menu / family seating", vegetarian: "Vegetarian options",
  vegan: "Vegan options", gluten_free: "Gluten-free options", happy_hour: "Happy hour",
  private_events: "Private events", live_music: "Live music", delivery_takeout: "Takeout / delivery",
  wifi: "Wi-Fi", wheelchair: "Wheelchair access", brunch: "Brunch",
}

const LeadsComp: React.FC = () => {
  const [businessname, setbusinessname] = useState('')
  const [contactname, setcontactname] = useState('')
  const [email, setemail] = useState('')
  const [websiteurl, setwebsiteurl] = useState('')
  const [businesstag, setbusinesstag] = useState('')

  const [userid, setuserid] = useState('')
  const [loading, setloading] = useState(false)
  const [loadingmessage, setloadingmessage] = useState('')
  const [suggestions, setsuggestions] = useState<any[]>([])

  const [leads, setleads] = useState<any[]>([])
  const [loaduserid, setloaduserid] = useState('')
  const [crawlstatus, setcrawlstatus] = useState('')
  const [crawlerror, setcrawlerror] = useState('')
  const [facts, setfacts] = useState<any>(null)

  const [modalstate, setmodalstate] = useState(false)
  const [modalheader, setmodalheader] = useState('')
  const [modalmessage, setmodalmessage] = useState('')

  const openModal = (header: string, message: string) => {
    setmodalheader(header)
    setmodalmessage(message)
    setmodalstate(true)
  }

  const loadLeadsList = async () => {
    try {
      let response = await fetchWrapper.post('/api/leads/list', {})
      setleads(response.leads || [])
    } catch (error) { }
  }

  useEffect(() => { loadLeadsList() }, [])

  const resetSelection = () => {
    setuserid(''); setbusinessname(''); setcontactname(''); setemail(''); setwebsiteurl(''); setbusinesstag('')
    setcrawlstatus(''); setcrawlerror(''); setfacts(null); setsuggestions([])
  }

  const createLead = async () => {
    if (!email || !websiteurl) {
      openModal('Warning', 'Email and website URL are required.')
      return
    }
    setloading(true)
    setloadingmessage('Creating lead...')
    try {
      let response = await fetchWrapper.post('/api/leads/create', {
        businessname, contactname, email, websiteurl, businesstag,
      })
      if (response.status !== 200) {
        openModal('Warning', response.msg)
      } else {
        setuserid(response.userid)
        setcrawlstatus('none')
        setcrawlerror('')
        openModal('Success', `Lead created: ${response.userid}. Now crawl their website.`)
        await loadLeadsList()
      }
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const loadLeadByUserid = async (uid: string) => {
    if (!uid) {
      openModal('Warning', 'Enter a userid to load.')
      return
    }
    setloading(true)
    setloadingmessage('Loading lead...')
    try {
      let response = await fetchWrapper.post('/api/leads/get', { userid: uid })
      if (response.status !== 200) {
        openModal('Warning', response.msg)
      } else {
        setuserid(response.userid)
        setbusinessname(response.businessname)
        setcontactname(response.contactname)
        setemail(response.email)
        setwebsiteurl(response.websiteurl)
        setbusinesstag(response.businesstag)
        setcrawlstatus(response.crawlstatus)
        setcrawlerror(response.crawlerror)
        setfacts(response.facts)
        setsuggestions([])
        setloaduserid('')
      }
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const reloadContent = async () => {
    if (!userid) return
    setloading(true)
    setloadingmessage('Re-crawling their site - this replaces the old content, can take a couple of minutes...')
    try {
      let response = await fetchWrapper.post('/api/leads/reload', { userid })
      if (response.status !== 200) {
        openModal('Warning', response.msg)
      } else {
        setcrawlstatus('crawling')
        openModal('Started', 'Reload started - check back in a minute or two by loading this lead again.')
      }
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const crawlSite = async () => {
    if (!userid || !websiteurl) {
      openModal('Warning', 'Create the lead first, then crawl.')
      return
    }
    setloading(true)
    setloadingmessage('Crawling site and generating context with Qwen - this can take a couple of minutes for a large site...')
    try {
      let response = await fetchWrapper.post('/api/leads/crawl', { userid, url: websiteurl })
      openModal(response.status === 200 ? 'Done' : 'Warning', response.msg)
      await loadSuggestions()
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const loadSuggestions = async () => {
    if (!userid) return
    try {
      let response = await fetchWrapper.post('/api/leads/getsuggestions', { userid, status: 'pending' })
      setsuggestions(response.suggestions || [])
    } catch (error) {
      openModal('Error', `${error}`)
    }
  }

  const updateSuggestionField = (id: string, field: 'topic' | 'context', value: string) => {
    setsuggestions((prev) => prev.map((s) => (s._id === id ? { ...s, [field]: value } : s)))
  }

  const publish = async (s: any) => {
    try {
      await fetchWrapper.post('/api/leads/publish', { id: s._id, topic: s.topic, context: s.context })
      setsuggestions((prev) => prev.filter((x) => x._id !== s._id))
    } catch (error) {
      openModal('Error', `${error}`)
    }
  }

  const reject = async (s: any) => {
    try {
      await fetchWrapper.post('/api/leads/reject', { id: s._id })
      setsuggestions((prev) => prev.filter((x) => x._id !== s._id))
    } catch (error) {
      openModal('Error', `${error}`)
    }
  }

  const sendDemo = async () => {
    if (!userid) return
    setloading(true)
    setloadingmessage('Sending demo email...')
    try {
      let response = await fetchWrapper.post('/api/leads/senddemo', { userid })
      openModal(response.status === 200 ? 'Sent' : 'Warning', response.msg)
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const attrEntries = Object.entries(facts?.attrs || {})
  const contactLine = () => {
    let c = facts?.contact || {}
    let parts = []
    if (c.email?.value) parts.push(c.email.value)
    if (c.phone?.value) parts.push(c.phone.value)
    return parts.join(' · ')
  }

  return (
    <Layout>
      <MessageModal state={modalstate} header={modalheader} message={modalmessage} handleClose={() => setmodalstate(false)} />

      <ListCol>
        <SectionTitle>Leads ({leads.length})</SectionTitle>
        <LeadList>
          {leads.map((l) => (
            <LeadRow key={l.userid} $active={l.userid === userid} onClick={() => loadLeadByUserid(l.userid)}>
              <LeadName>{l.firstname || l.userid}</LeadName>
              <LeadMeta>{l.websiteurl}</LeadMeta>
              <StatusBadge $status={l.crawlstatus || 'none'}>{l.crawlstatus || 'none'}</StatusBadge>
            </LeadRow>
          ))}
          {!leads.length && <SectionHint>No leads yet - create one below.</SectionHint>}
        </LeadList>

        <FormRow>
          <InputComp id="lead_load_userid" value={loaduserid} onChange={(e: any) => setloaduserid(e.target.value)} placeholder="or load by userid" width={180} />
          <SmallButton text="Load" handleClick={() => loadLeadByUserid(loaduserid)} />
        </FormRow>

        <SectionTitle>Create a lead</SectionTitle>
        <FormRow>
          <InputComp id="lead_businessname" value={businessname} onChange={(e: any) => setbusinessname(e.target.value)} placeholder="Business name" />
          <InputComp id="lead_contactname" value={contactname} onChange={(e: any) => setcontactname(e.target.value)} placeholder="Contact name" />
        </FormRow>
        <FormRow>
          <InputComp id="lead_email" value={email} onChange={(e: any) => setemail(e.target.value)} placeholder="Contact email" />
          <InputComp id="lead_businesstag" value={businesstag} onChange={(e: any) => setbusinesstag(e.target.value)} placeholder="Business tag" />
        </FormRow>
        <FormRow>
          <InputComp id="lead_websiteurl" value={websiteurl} onChange={(e: any) => setwebsiteurl(e.target.value)} placeholder="https://theirwebsite.com" width={280} />
        </FormRow>
        <FormRow>
          <Button text="Create Lead" handleClick={createLead} />
          {userid && <SmallButton text="Clear selection" handleClick={resetSelection} backgroundColor={theme.colors.textMuted} />}
        </FormRow>

        {loading && (
          <LoadingRow>
            <LoadingComponent />
            <div>{loadingmessage}</div>
          </LoadingRow>
        )}
      </ListCol>

      {userid ? (
        <DetailCol>
          <FormRow>
            <UseridBadge>userid: {userid}</UseridBadge>
            {crawlstatus && (
              <StatusBadge $status={crawlstatus}>
                {crawlstatus === 'ready' ? 'content ready' : crawlstatus === 'crawling' ? 'crawling…' : crawlstatus === 'error' ? `error: ${crawlerror || 'crawl failed'}` : 'not crawled yet'}
              </StatusBadge>
            )}
            {(crawlstatus === 'ready' || crawlstatus === 'error') && (
              <SmallButton text="Reload site content" handleClick={reloadContent} backgroundColor={theme.colors.accent} />
            )}
          </FormRow>

          <SectionTitle>Invite them to try it</SectionTitle>
          <SectionHint>
            No need to crawl first - the site is read automatically the first time they open their link.
          </SectionHint>
          <Button text="Email Demo Link" handleClick={sendDemo} />

          <SectionTitle>Extracted facts</SectionTitle>
          {!facts && <SectionHint>No facts extracted yet - crawl or reload the site first, then load this lead again.</SectionHint>}
          {facts && (
            <FactsGrid>
              <FactCard>
                <FactName>Contact</FactName>
                <FactValue>{contactLine() || 'not found'}</FactValue>
              </FactCard>
              {attrEntries.map(([key, v]: any) => (
                <FactCard key={key}>
                  <FactName>{ATTR_LABELS[key] || key}</FactName>
                  <Quote>&ldquo;{v.quote}&rdquo;</Quote>
                </FactCard>
              ))}
            </FactsGrid>
          )}

          <SectionTitle>Optional: preview &amp; stage content yourself first</SectionTitle>
          <SectionHint>
            Only needed if you want to review what the crawler finds before the prospect ever sees it.
          </SectionHint>
          <FormRow>
            <Button text="Crawl & Generate Context" handleClick={crawlSite} />
            <SmallButton text="Refresh Suggestions" handleClick={loadSuggestions} />
          </FormRow>

          {suggestions.length > 0 && (
            <>
              <SectionTitle>Review suggestions ({suggestions.length} pending)</SectionTitle>
              {suggestions.map((s) => (
                <SuggestionCard key={s._id}>
                  <SuggestionUrl>{s.url}</SuggestionUrl>
                  <InputComp id={`topic_${s._id}`} value={s.topic} onChange={(e: any) => updateSuggestionField(s._id, 'topic', e.target.value)} placeholder="Topic" />
                  <TextArea
                    value={s.context}
                    onChange={(e) => updateSuggestionField(s._id, 'context', e.target.value)}
                  />
                  <FormRow>
                    <SmallButton text="Approve & Publish" handleClick={() => publish(s)} />
                    <SmallButton text="Reject" handleClick={() => reject(s)} backgroundColor={theme.colors.danger} />
                  </FormRow>
                </SuggestionCard>
              ))}
            </>
          )}
        </DetailCol>
      ) : (
        <DetailCol>
          <SectionHint>Select a lead on the left, or create a new one.</SectionHint>
        </DetailCol>
      )}
    </Layout>
  )
}

const Layout = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: flex-start;
  color: ${theme.colors.text};
  @media (max-width: 900px) {
    flex-direction: column;
  }
`

const ListCol = styled.div`
  display: flex;
  flex-direction: column;
  width: 320px;
  flex-shrink: 0;
  gap: 6px;
  @media (max-width: 900px) {
    width: 100%;
  }
`

const DetailCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 6px;
`

const LeadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
`

const LeadRow = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: ${theme.radius.md};
  border: 1px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$active ? theme.colors.primarySoft : theme.colors.surface)};
  cursor: pointer;
`

const LeadName = styled.div`
  font-size: 13px;
  font-weight: 700;
`

const LeadMeta = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-top: 16px;
`

const SectionHint = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  margin-bottom: 4px;
`

const FormRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`

const UseridBadge = styled.div`
  align-self: flex-start;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.primarySoft};
  padding: 4px 10px;
  border-radius: ${theme.radius.sm};
`

const STATUS_COLORS: { [key: string]: string } = {
  ready: theme.colors.success,
  crawling: theme.colors.textMuted,
  error: theme.colors.danger,
  none: theme.colors.textMuted,
}

const StatusBadge = styled.div<{ $status: string }>`
  align-self: flex-start;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: ${(p) => STATUS_COLORS[p.$status] || theme.colors.textMuted};
  padding: 3px 8px;
  border-radius: ${theme.radius.sm};
`

const LoadingRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: ${theme.colors.textMuted};
`

const FactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`

const FactCard = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FactName = styled.div`
  font-weight: 800;
  font-size: 11px;
`

const FactValue = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

const Quote = styled.div`
  font-size: 10px;
  color: ${theme.colors.textMuted};
`

const SuggestionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadow.card};
  padding: 14px;
`

const SuggestionUrl = styled.a`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border-radius: ${theme.radius.sm};
  border: 1px solid ${theme.colors.border};
  font-family: inherit;
  font-size: 13px;
  resize: vertical;
`

export default LeadsComp
